import { ListContextProvider } from "ra-core";
import { FC, useState } from "react";
import { Link as RouterLink } from 'react-router-dom'
import useLocalListController from "@/components/list/useLocalListController";
import { RecPatch } from "@/dataProvider/tools";
import { FileEncryptionTemplateDto, FileTemplateKind, FileTemplateStatus } from "@/gen/api/encrypt";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Card, Box, Typography, Badge, IconButton, Stack, Tooltip, Button } from "@mui/material";
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import TextField from "@/components/field/TextField";
import DateField2 from "@/components/field/DateField2";
import FunctionField from "@/components/field/FunctionField";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import AddLinkIcon from '@mui/icons-material/AddLink';
import KeyIcon from '@mui/icons-material/Key';
import EditIcon from '@mui/icons-material/Edit';
import MappedIcon from "@/icons/Mapped";
import { genUrlWithHashid } from "@/utils/hashid";
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "@/components/list/datagridFunctionWidgets";
import UploadFileTemplate from "@/components/obfuscation/UploadFileTemplate";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_ONDATA_CLIENTS } from "@/constants";


const resource = 'file-encrypt-template'

const fileTemplateListOptions : {sorters: Record<string, (a:FileEncryptionTemplateDto & RecPatch, b:FileEncryptionTemplateDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '').toUpperCase() < (b.name || '').toUpperCase() ? -1 : 1,
    uploadTime: (a, b) => (a.uploadTime || '') < (b.uploadTime || '') ? -1 : 1,
  }
}

const templateKindOptions : FilterOption<FileTemplateKind>[] = [
  { value: FileTemplateKind.CSV,        label: 'CSV File' },
  { value: FileTemplateKind.FixedWidth, label: 'Fixed Width File' },
  { value: FileTemplateKind.JsonSample, label: 'Json File' },
  { value: FileTemplateKind.EDI,        label: 'EDI File' },
  { value: FileTemplateKind.ParquetSample,  label: 'Parquet File' },
]

const FileMappingList: FC = () => {
  const hasClassifyPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION]),
    hasClientPermit = useHasPermit([MANAGE_ONDATA_CLIENTS])
  const listCtrl = useLocalListController({ resource, basePath: '' }, fileTemplateListOptions)
  const [open, setOpen] = useState(false)
  
  return (
    <ListContextProvider value={listCtrl}>
      
      <Card>
        <AtopFuncArea >
          <SearchField 
            sourceKey="name"
            match={(d: FileEncryptionTemplateDto, fval) => !!d.name && d.name.toLowerCase().includes((fval as string).trim().toLowerCase())}
            sx={{ width: 280 }}
            TextFieldProps={{ placeholder: 'Search by File Template Name' }}
          />
          <FilterField 
            label="Template Type" 
            filterOptions={templateKindOptions} 
            sourceKey="kind"
            match={(d: FileEncryptionTemplateDto, fval:FilterOption|'_empty_') => {
              if (fval === '_empty_') return true
              const value = fval.value
              return d.kind === value
            }}
            sx={{ width: 320 }}
          />
          {hasClassifyPermit &&
            <Button variant="contained" color="primary" sx={{ml:'auto', mr: 2}}
              onClick={() => setOpen(true)}
            >
              Upload
            </Button>}
        </AtopFuncArea>
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
              }}
              {...listCtrl}
            >
              <TextField source="name" label="Template Name" sortable={true} sx={{ textTransform: 'uppercase' }} />
              <FunctionField source="kind" label="Type" sortable={false} 
                render={(d?:FileEncryptionTemplateDto&RecPatch) => (
                  d && <Typography variant="body2">{
                    d.kind === FileTemplateKind.CSV ? "CSV File" 
                    : d.kind === FileTemplateKind.FixedWidth ? "Fixed Width File"
                    : d.kind === FileTemplateKind.JsonSample ? "Json File"
                    : d.kind === FileTemplateKind.EDI        ? "EDI File"
                    : d.kind === FileTemplateKind.ParquetSample ? "Parquet File"
                    : ("not supported")
                  }</Typography>
                )}
              />
              <DateField2 source="uploadTime" label="Upload Time" sortable={true} />
              <FunctionField source="columnCounts" label="Fields" sortable={false} 
                render={(d?:FileEncryptionTemplateDto&RecPatch) => {
                  if (!d) { return null }
                  const [mappedCount, totalCount, obfsCount] = d.columnCounts || [0, 0, 0]
                  const keyNotAssigned = d.status === FileTemplateStatus.Uploaded || d.status === FileTemplateStatus.ColumnsLabeled
                  const columnNotLabeled = d.status === FileTemplateStatus.Uploaded || d.status === FileTemplateStatus.KeyAssigned

                  return (
                    <Stack direction="row" spacing={0} >
                      <Tooltip title={keyNotAssigned ? "Key not assigned" : "Key assigned"}>
                        <IconButton color={"info" as any} className={keyNotAssigned ? 'Mui-disabled' : undefined}
                          sx={{ transform: 'rotate(90deg)' }} size="small"
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Encrypted ${mappedCount}/${totalCount}`}>
                        <IconButton color={("inherit") as any} className={columnNotLabeled ? 'Mui-disabled' : undefined}
                           size="small"
                        >
                          <Badge color={"success" as any} badgeContent={mappedCount} max={9999}>
                            <MappedIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Obfuscated ${obfsCount ?? 0}/${totalCount}`}>
                        <IconButton color={"inherit"} size="small">
                          <Badge color="secondary" badgeContent={obfsCount ?? 0} max={9999}>
                            <BlurLinearIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )
                }}
              />
              <FunctionField source="status" label="Action " sortable={false} 
                render={(d?:FileEncryptionTemplateDto&RecPatch) => {
                  if (!d) { return null }
                  return (
                    <Stack direction="row"
                      sx={{
                        '& .MuiIconButton-root': {
                          color: 'text.primary' as any, 
                          '&:hover': { backgroundColor: 'action.hover' },
                        }
                      }}
                    >
                    {hasClientPermit &&
                      <Tooltip title="Assign Clients">
                        <IconButton
                          component={RouterLink}
                          to={genUrlWithHashid(`/encryption/file-mappings/$0/clients`,
                            d?.templateId || 0, d?.name
                          )}
                        >
                          <AddLinkIcon />
                        </IconButton>
                      </Tooltip>}
                    <Tooltip title={hasClassifyPermit ? "Edit" : "View"}>
                      <IconButton 
                        sx={{ 
                          color: 'text.primary' as any, 
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        component={RouterLink}
                        to={genUrlWithHashid(`/encryption/file-mappings/$0/show`, d?.templateId || 0, d?.name)}
                      >
                        {hasClassifyPermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    </Stack>
                  )
                }}
              />

            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtrl} />}
      </Card>
      {hasClassifyPermit && <UploadFileTemplate open={open} setOpen={setOpen} />}
    </ListContextProvider>
  )
}

export default FileMappingList;
